import { OpenIDConnectConfig } from "@closerplatform/spinner-openapi";
import { staticConfig } from "@fidget/common/config/static-config";
import { SafeLocalStorageService } from "@fidget/common/services/safeLocalStorageService";
import { logger, uuidv4 } from "@fidget/common/utils";
import { Path } from "../models/router";
import { SpinnerService } from "./spinner/spinnerService";

interface AuthParams extends OpenIDConnectConfig {
  nonce: string;
  state: string;
  orgId: string;
}

export class OIDCService {
  public static scope = "openid";
  public static responseType = "code";
  public static redirectUriPath = Path.LOGIN_OIDC_RESPONSE;

  private static localStorageKey = staticConfig.localStorageKeyPrefix + ":oidc-auth";

  constructor(private spinnerService: SpinnerService) {}

  public static getUrlOrgIdParam(paramName = staticConfig.oidcLoginUrlOrgIdParamName) {
    const params = new URL(window.location.href).searchParams;

    return params.get(paramName);
  }

  public static get redirectUri(): string {
    return window.location.origin + this.redirectUriPath;
  }

  public async redirectAuthorization(orgId: string) {
    try {
      const [config, { nonce }] = await Promise.all([
        this.spinnerService.getOIDCConfig(orgId),
        this.spinnerService.getNonce()
      ]);

      const state = OIDCService.generateState();
      const authParams = { ...config, nonce, state, orgId };
      this.saveParams(authParams);

      window.location.replace(OIDCService.prepareAuthUrl(authParams));
    } catch (e) {
      logger.error({ message: "Couldn't load OIDC config for org", orgId, error: e });

      throw e;
    }
  }

  public loadSavedParams(): AuthParams {
    const storage = SafeLocalStorageService.getInstance();

    const item = storage.getItem(OIDCService.localStorageKey);
    if (!item) {
      throw new Error("No saved OpenID Connect params");
    }

    try {
      return JSON.parse(item);
    } catch (e) {
      logger.error("Couldn't parse saved OpenID Connect params");

      throw e;
    }
  }

  public clearSavedParams() {
    SafeLocalStorageService.getInstance().removeItem(OIDCService.localStorageKey);
  }

  private static prepareAuthUrl({ clientId, authUrl, nonce, state }: AuthParams) {
    const url = new URL(authUrl);
    url.searchParams.append("client_id", clientId);
    url.searchParams.append("response_type", this.responseType);
    url.searchParams.append("redirect_uri", this.redirectUri);
    url.searchParams.append("scope", this.scope);
    url.searchParams.append("nonce", nonce);
    url.searchParams.append("state", state);

    return url.toString();
  }

  private static generateState() {
    return `oidcstate-${uuidv4()}`;
  }

  private saveParams(params: AuthParams) {
    const storage = SafeLocalStorageService.getInstance();

    storage.setItem(OIDCService.localStorageKey, JSON.stringify(params));
  }
}
